<template>
  <div class="type-widget">
    <author-toolbar
      v-if="loggedInUser && loggedInUser.capabilities.includes('publish_posts')"
      :post="resource"
    />

    <local-version-banner v-if="resource.local_version" :content="resource.local_version" />

    <div
      v-if="resource.header_bg_image"
      class="section-resource-banner"
      :style="{ backgroundImage: 'url(' + resource.header_bg_image + ')' }"
    >
      <responsive-image
        :image="resource.media_thumbnail || resource.media[0]"
        alt="Widget title image"
      />
    </div>

    <div class="row">
      <div class="columns small-12 medium-8 large-7 small-order-2 medium-order-1">
        <header>
          <div :class="['text-' + resource.type, 'subheader']">Widget</div>
          <h1>{{ resource.name }}</h1>

          <nav v-if="resource.breadcrumbs" aria-label="breadcrumbs">
            <ol class="breadcrumbs">
              <li>
                In
                <nuxt-link :to="localePath('/widgets/')" title="Go to page">Widgets</nuxt-link>
              </li>
            </ol>
          </nav>
        </header>

        <div class="section section-actions flex flex-wrap align-middle">
          <a
            v-if="hasPermissions"
            :href="resource.widget_link ? resource.widget_link : '/engage/' + resource.slug + '/'"
            class="button button-primary"
            title="Use widget"
            @click="handleClick()"
          >
            <svg
              v-if="!resource.widget_link.includes('studio')"
              class="icon icon-small"
              role="img"
              aria-hidden="true"
              aria-label="Play widget"
            >
              <use xlink:href="/images/fa-icons.svg#play"></use>
            </svg>
            {{ resource.widget_link.includes('studio') ? 'Go to Studio' : 'Play' }}
          </a>
          <a
            v-else
            :href="localePath('/plans/')"
            class="button button-primary"
            title="Go to plans page"
            @click="eventUpgradeClicked()"
          >
            <svg class="icon icon-small" role="img" aria-hidden="true" aria-label="Play widget">
              <use xlink:href="/images/fa-icons.svg#lock"></use>
            </svg>
            Upgrade to use
          </a>
          <action-buttons class="pull-right" :resource-id="resource.id" />
        </div>

        <div class="callout callout-details">
          <publisher-link v-if="resource.publisher" :publisher="resource.publisher" />

          <template v-if="resource.year_levels_range">
            Suitable for {{ $t('grade') }}s:&nbsp;
            <strong>{{ resource.year_levels_range }}</strong>
          </template>
        </div>

        <resource-content :resource="resource" />

        <section
          v-if="resource.curriculums && resource.curriculums.length > 0"
          class="section section-curriculum"
        >
          <h3>Curriculum</h3>
          <curriculum-list :curriculums="resource.curriculums" />
        </section>

        <tag-list
          :file-formats="resource.file_formats"
          :tags="resource.tags"
          :year-levels="resource.year_levels"
          :country="resource.country"
        />
      </div>

      <aside class="columns small-12 medium-4 large-5 small-order-1 medium-order-2">
        <div>
          <figure v-if="!resource.header_bg_image">
            <responsive-image
              :image="resource.media_thumbnail || resource.media[0]"
              alt="Widget title image"
              loading="lazy"
            />
          </figure>
        </div>
      </aside>
    </div>

    <comment-section
      class="section-comments"
      :resource-id="resource.id"
      :comment-count="resource.comments"
      :resource-updates="resource.updates"
      :event-params="resource.event_params"
    />

    <resources-list
      align="center"
      :load-more="true"
      :params="{ id: resource.id }"
      list-type="related"
    />
    <country-switcher :link="resource.link" :linked="resource.linked" :country="resource.country" />
  </div>
</template>

<script>
/* A widget
 *
 * @parent:
 * @requests:
 * @events:
 * @props:
 * @components: ActionButtons, AuthorToolbar, TagList, CommentSection, CurriculumList, ImageGallery, ResourcesList
 * @methods:
 */

import { mapGetters } from 'vuex';
import ActionButtons from '~/components/resource/ActionButtons.vue';
import TagList from '~/components/resource/TagList.vue';
import CommentSection from '~/components/comment/CommentSection.vue';
import ResourcesList from '~/components/resource/ResourcesList.vue';
import CurriculumList from '~/components/resource/CurriculumList.vue';
import ResponsiveImage from '~/components/ResponsiveImage.vue';
import PublisherLink from '~/components/PublisherLink.vue';
import ResourceContent from '~/components/resource/ResourceContent.vue';
import CountrySwitcher from '~/components/CountrySwitcher.vue';

export default {
  name: 'WidgetResource',
  components: {
    ActionButtons,
    AuthorToolbar: () => import('~/components/AuthorToolbar.vue'),
    CommentSection,
    LocalVersionBanner: () => import('~/components/resource/LocalVersionBanner.vue'),
    TagList,
    ResourcesList,
    CurriculumList,
    ResponsiveImage,
    ResourceContent,
    PublisherLink,
    CountrySwitcher,
  },
  validate(_params) {
    // TODO: Should this be turned back on?
    return true;
    //return /\s/g.test(params.slug);
  },
  async asyncData({ app, store, route, params, error }) {
    const data = { types: ['widget'] };

    try {
      const resource = await store.dispatch('loadResource', {
        id: params.slug,
        types: data.types,
        link: route.path,
        country: app.i18n.locale,
      });

      data.title = resource.name || resource.meta_title;
    } catch (err) {
      // No page matching this route
      error({ statusCode: 404, message: err.message, path: route.path });
    }

    return data;
  },
  data() {
    return {
      title: 'Widget',
      description: 'A widget to use in your classroom',
    };
  },
  jsonld() {
    return this.$jsonSchemaResource(this.resource);
  },
  head() {
    return {
      title: this.title,
      meta: this.$metaPropertiesResource(this.resource),
      link: this.$hrefLangsResource(
        this.resource.linked,
        this.resource.link,
        this.resource.country
      ),
    };
  },
  computed: {
    ...mapGetters(['loggedInUser', 'resource']),
    hasPermissions() {
      // Check if user can access this widget
      let permitted = false;

      if (this.loggedInUser) {
        // Widgets have a separate 'availability' level to resources to check against:
        // free
        // access_product_widgets (Essential)
        // access_product_widgets_plus (Plus)
        if (
          this.resource.availability === 'free' ||
          (this.loggedInUser.capabilities &&
            this.loggedInUser.capabilities.includes(this.resource.availability))
        ) {
          permitted = true;
        }
      }

      return permitted;
    },
  },
  mounted() {
    if (this.resource.event_params) {
      this.$mzAnalyticsApi({
        event: 'pageView',
        name: 'widget',
        productArea: 'widget',
        entityType: this.resource.type,
        entityId: this.resource.id,
      });
    }
  },
  methods: {
    handleClick() {
      this.$mzAnalyticsApi({
        event: 'click',
        name: 'widget.play',
        productArea: 'widget',
        entityType: this.resource.type,
        entityId: this.resource.id,
      });
    },
    eventUpgradeClicked() {
      const eventType = this.resource.event_params.Resource_Type_Name ? 'Resource' : 'Content';
      const upgradeParams = {
        Click_Text: 'Upgrade to use',
        Click_Type: 'Button',
        Click_Action: 'Send user to page | Plans and Pricing',
        Click_Page_Position: 'Content | Main CTA',
      };
      this.$mzAnalyticsApi({
        event: 'click',
        name: 'widget.upgrade_to_use',
        productArea: 'widget',
        entityType: this.resource.type,
        entityId: this.resource.id,
      });
    },
  },
};
</script>

<style scoped lang="scss">
@import '~/assets/scss/sections/_resource-page.scss';
</style>
